import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'options',
    'selectedElement',
    'input',
    'focus',
    'container',
  ];

  connect() {
    const preSelectedElement = Array.from(
      this.optionsTarget.querySelectorAll('li'),
    ).find((option) => option.dataset.value === this.inputTarget.value);
    this.#markSelected(preSelectedElement);
  }

  handleToggle(event) {
    if (event.newState === 'open') {
      this.focusTarget.focus();
    }
  }

  #hide = () => {
    if (this.optionsTarget.matches(':popover-open')) {
      this.optionsTarget.hidePopover();
    }
  };

  selectOptionElementWithEnter = (event) => {
    if (this.#containEventAndCheckFocus(event, ['Enter'])) {
      const selectedOption = this.#getSelectedOptionElement();
      if (selectedOption) this.#selectOption(selectedOption);
    }
  };

  switchOptionElementsWithArrowKeys = (event) => {
    if (this.#containEventAndCheckFocus(event, ['ArrowDown', 'ArrowUp'])) {
      if (!this.optionsTarget.matches(':popover-open')) {
        this.optionsTarget.showPopover();
      }
      const alreadySelectedElement = this.#getSelectedOptionElement();
      this.#unmarkSelected(alreadySelectedElement);
      if (event.code === 'ArrowDown') {
        this.#selectNextElement(alreadySelectedElement);
      } else if (event.code === 'ArrowUp') {
        this.#selectPreviousElement(alreadySelectedElement);
      }
    }
  };

  #selectNextElement = (alreadySelectedElement) => {
    if (!alreadySelectedElement) {
      this.#markSelected(this.optionsTarget.firstElementChild);
      return;
    }
    const nextElement = this.#isLastChild(alreadySelectedElement)
      ? alreadySelectedElement.parentElement.firstElementChild
      : alreadySelectedElement.nextElementSibling;
    this.#markSelected(nextElement);
  };

  #selectPreviousElement = (alreadySelectedElement) => {
    if (!alreadySelectedElement) {
      this.#markSelected(this.optionsTarget.lastElementChild);
      return;
    }
    const previousElement = this.#isFirstChild(alreadySelectedElement)
      ? alreadySelectedElement.parentElement.lastElementChild
      : alreadySelectedElement.previousElementSibling;
    this.#markSelected(previousElement);
  };

  #getSelectedOptionElement = () =>
    this.optionsTarget.querySelector('li[data-selected]');

  #markSelected = (element) => {
    element?.setAttribute('data-selected', '');
  };

  #unmarkSelected = (element) => {
    element?.removeAttribute('data-selected');
  };

  #containEventAndCheckFocus = (event, expectedCodes) => {
    if (
      expectedCodes.includes(event.code) &&
      this.focusTarget === document.activeElement
    ) {
      event.preventDefault();
      return true;
    }
    return false;
  };

  selectOptionByClick = (event) => {
    this.#selectOption(event.currentTarget);
  };

  #selectOption = (selectedOption) => {
    this.#showSelectedElement(selectedOption.firstElementChild.cloneNode(true));
    this.#prepareAndHideOptionsList(selectedOption);
    const oldValue = this.inputTarget.value;
    this.inputTarget.value = selectedOption.getAttribute('data-value');
    if (this.inputTarget.value !== oldValue) {
      this.dispatch('selectInputValueChanged', {
        detail: {
          newValue: this.inputTarget.value,
          inputId: this.inputTarget.id,
        },
        prefix: false,
      });
    }
    this.focusTarget.focus();
  };

  #showSelectedElement = (selectedElement) => {
    this.selectedElementTarget.replaceChild(
      selectedElement,
      this.selectedElementTarget.firstElementChild,
    );
  };

  #prepareAndHideOptionsList = (selectedElement) => {
    this.#unmarkSelected(this.#getSelectedOptionElement());
    this.#markSelected(selectedElement);
    this.#hide();
  };

  #isFirstChild = (element) => element.previousElementSibling == null;

  #isLastChild = (element) => element.nextElementSibling == null;
}
